import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/sharp-regular-svg-icons';
import {
  faChevronDown,
  faSearch,
  faSpinner,
  faSquareCheck,
  faXmark,
} from '@fortawesome/sharp-solid-svg-icons';
import clsx from 'clsx';
import React from 'react';
import {
  ClearIndicatorProps,
  components,
  ControlProps,
  GroupHeadingProps,
  OptionProps,
  ValueContainerProps,
} from 'react-select';

import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';

export const ControlWithAdornment = ({ children, ...props }: ControlProps) => {
  return (
    <components.Control {...props}>
      <FontAwesomeIcon className="mr-3.5 text-gray-70" icon={faSearch} />
      {children}
    </components.Control>
  );
};

export const Control = ({ children, ...props }: ControlProps) => {
  return <components.Control {...props}>{children}</components.Control>;
};
export const ClearIndicator = ({
  innerProps: { ref, ...restInnerProps },
}: ClearIndicatorProps) => {
  return (
    <div ref={ref} {...restInnerProps}>
      <FontAwesomeIcon
        className="mr-3.5 text-gray-70"
        icon={faXmark}
        size="lg"
      />
    </div>
  );
};
export const GroupHeading = ({ children, ...props }: GroupHeadingProps) => (
  <div className="text-red-500">
    <components.GroupHeading {...props}>
      <Typography className="text-gray-70" variant="h7">
        {children}
      </Typography>
    </components.GroupHeading>
  </div>
);
export const LoadingIndicator = () => {
  return (
    <FontAwesomeIcon
      spinPulse
      className="mr-3.5 text-gray-70"
      icon={faSpinner}
    />
  );
};

export const DropdownIndicator = () => {
  return <FontAwesomeIcon className="ml-2" icon={faChevronDown} />;
};

type CustomOptionProps = OptionProps & { hideToolTip?: boolean };

export const Option = ({
  children,
  innerProps,
  innerRef,
  isMulti,
  isFocused,
  isSelected,
  hideToolTip = false,
}: CustomOptionProps) => {
  return (
    <div
      ref={innerRef}
      className={clsx(
        'flex min-h-[48px] items-center px-4 py-3 text-gray-70 hover:cursor-pointer hover:text-gray-100',
        isFocused ? 'bg-gray-20' : 'bg-white'
      )}
      data-tooltip-content={hideToolTip ? undefined : (children as string)}
      data-tooltip-id={GLOBAL_TOOLTIP}
      {...innerProps}
    >
      {isMulti && (
        <FontAwesomeIcon
          className="mr-2 text-gray-100 hover:text-gray-100"
          icon={isSelected ? faSquareCheck : faSquare}
        />
      )}

      <Typography
        className="max-w-[100%] overflow-hidden text-ellipsis whitespace-nowrap"
        variant="body1"
      >
        {children}
      </Typography>
    </div>
  );
};

export const OptionNoToolTip = (props: OptionProps) => {
  return <Option {...props} hideToolTip />;
};

export const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
  if (props.isMulti) {
    const { value: multiValue } = props.selectProps;
    let val = '';
    if (multiValue && multiValue instanceof Array) {
      val = multiValue?.map((p) => p.label).join(', ');
    }
    return (
      <components.ValueContainer {...props}>
        <div
          className="inline overflow-hidden text-ellipsis whitespace-nowrap"
          data-testid="multi-value-container"
          style={{ gridArea: '1/1/2/3' }}
        >
          {!props.selectProps.inputValue ? val : null}
        </div>

        {React.Children.map(children, (child) => {
          if (
            child &&
            React.isValidElement(child) &&
            typeof child.type !== 'string'
          ) {
            if (
              child.type.name === 'Placeholder' ||
              child.type.name === 'Input'
            ) {
              return child;
            }
          }

          return null;
        })}
      </components.ValueContainer>
    );
  }

  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  );
};
